import type {
  ConfigPublica,
  DatosTarjeta,
  ErrorApi,
  MisSesiones,
  ReservaCreada,
  ResultadoPago,
  VistaPublica,
} from '../../../compartido/api';
import type { DisponibilidadMes, Fecha, Hora } from '../../../compartido/tipos';

/**
 * Cliente de la API. Mismo origen: CloudFront manda /api/* a API Gateway.
 * Toda respuesta que no es 2xx se vuelve un ErrorDeApi con el motivo que
 * mandó el servidor; un corte de red también, con motivo SIN_CONEXION.
 */

export class ErrorDeApi extends Error {
  constructor(
    readonly estado: number,
    readonly cuerpo: ErrorApi,
  ) {
    super(cuerpo.mensaje ?? cuerpo.error);
    this.name = 'ErrorDeApi';
  }

  get motivo(): string {
    return this.cuerpo.error;
  }
}

async function pedir<T>(metodo: 'GET' | 'POST', ruta: string, cuerpo?: unknown, token?: string): Promise<T> {
  let r: Response;
  try {
    r = await fetch(`/api${ruta}`, {
      method: metodo,
      headers: {
        ...(cuerpo !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
      cache: 'no-store',
    });
  } catch {
    throw new ErrorDeApi(0, { error: 'SIN_CONEXION', mensaje: 'No pudimos conectarnos. Revisa tu internet e intenta de nuevo.' });
  }

  let json: unknown;
  try {
    json = await r.json();
  } catch {
    json = undefined;
  }
  if (!r.ok) {
    const e = (json ?? {}) as Partial<ErrorApi>;
    throw new ErrorDeApi(r.status, { error: e.error ?? 'ERROR_INTERNO', ...e });
  }
  return json as T;
}

export const api = {
  config: () => pedir<ConfigPublica>('GET', '/config'),
  disponibilidad: (mes: string) => pedir<DisponibilidadMes>('GET', `/disponibilidad?mes=${encodeURIComponent(mes)}`),
  reservar: (datos: { fecha: Fecha; horas: Hora[]; correo: string; tema?: string }) =>
    pedir<ReservaCreada>('POST', '/reservas', datos),
  reserva: (id: string) => pedir<VistaPublica>('GET', `/reservas/${encodeURIComponent(id)}`),
  soltar: (id: string) => pedir<Record<string, never>>('POST', `/reservas/${encodeURIComponent(id)}/soltar`),
  pagar: (id: string, tarjeta: DatosTarjeta) => pedir<ResultadoPago>('POST', `/reservas/${encodeURIComponent(id)}/pago`, { tarjeta }),
  acceso: (correo: string) => pedir<Record<string, never>>('POST', '/acceso', { correo }),
  misSesiones: (token: string) => pedir<MisSesiones>('GET', '/mis-sesiones', undefined, token),
  reprogramar: (token: string, id: string, fecha: Fecha, horas: Hora[]) =>
    pedir<Record<string, never>>('POST', `/reservas/${encodeURIComponent(id)}/reprogramacion`, { fecha, horas }, token),
};

/** Mensajes para los motivos que el cliente puede ver. Lo que no está acá usa el mensaje del servidor. */
const MENSAJES: Record<string, string> = {
  HORAS_OCUPADAS: 'Alguien se adelantó y tomó una de esas horas. Elige otra.',
  RETENCION_VENCIDA: 'Pasaron los 15 minutos y las horas se liberaron. Vuelve a elegirlas.',
  COBRO_EN_CURSO: 'Ya hay un pago en proceso para esta reserva.',
  YA_PAGADA: 'Esta reserva ya está pagada.',
  DEMASIADOS_INTENTOS: 'Hiciste muchos intentos seguidos. Espera unos minutos y vuelve a probar.',
  NO_AUTORIZADO: 'Tu enlace venció o no es válido. Pide uno nuevo con tu correo.',
  NO_EXISTE: 'No encontramos esa reserva.',
  MEDIO_NO_PERMITIDO: 'Solo aceptamos tarjetas de crédito, débito o prepago.',
  CAMBIO_CONCURRENTE: 'Tu sesión cambió mientras elegías. Revisa cómo quedó y vuelve a intentarlo.',
  SIN_CAMBIOS_DISPONIBLES: 'Ya usaste los 2 cambios de esta sesión.',
  DEMASIADO_TARDE: 'Faltan menos de 24 horas: la sesión ya no se puede mover.',
  ERROR_INTERNO: 'Algo falló de nuestro lado. Intenta de nuevo en un momento.',
};

export function mensajeDeError(e: unknown): string {
  if (e instanceof ErrorDeApi) return MENSAJES[e.motivo] ?? e.cuerpo.mensaje ?? MENSAJES['ERROR_INTERNO']!;
  return MENSAJES['ERROR_INTERNO']!;
}
